/**
 * A run of multi-move puzzles, played entirely in this tab.
 *
 * The shape of the thing: you are shown a position that is winning, and you
 * have to play *the whole line* — every move of it, against a defence that
 * fights back. Finding one strong move is a hint; seeing three in a row is the
 * skill the puzzle is for.
 *
 * Two decisions the rest of this file follows from:
 *
 *  - **The next puzzle is hunted while you solve this one.** Proving a position
 *    takes seconds, and a screen that made you watch a spinner between puzzles
 *    would break the run. The worker is asked for the next one the moment the
 *    current one appears.
 *  - **A wrong move is not a game over.** It is the moment the player is most
 *    ready to learn, so the board holds still, says what the move cost, and
 *    offers the position back. Nothing here is rated or recorded.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { Puzzle, PuzzleVerdict } from '../../../games/analysis/puzzle.ts';
import { legalMoves } from '../../../games/spec/interpreter.ts';
import { applyMove, createInitial } from '../../../games/spec/interpreter.ts';
import type { SeatOf, State } from '../../../games/spec/interpreter.ts';
import type { GameSpec, RulesSpec } from '../../../games/spec/schema.ts';
import type { Seat } from '../../../shared/domain.ts';
import type { PuzzleRequest, PuzzleResponse } from './protocol.ts';

type Rules = RulesSpec;

/** Where the current puzzle stands. Drives every word on the screen. */
export type PuzzlePhase =
  /** No puzzle yet: the first hunt is running. */
  | 'seeking'
  /** A puzzle is on the board and it is your move. */
  | 'solving'
  /** Your move is being judged and answered. */
  | 'checking'
  /** You threw the win away. The position is still there to retry. */
  | 'wrong'
  /** You played the whole line. */
  | 'solved'
  /** Nothing could be found, or the engine failed. */
  | 'stuck';

export interface PuzzleRun {
  readonly spec: GameSpec;
  readonly puzzle: Puzzle | null;
  readonly phase: PuzzlePhase;
  readonly game: State<Rules>;
  readonly board: readonly number[];
  readonly legalColumns: readonly number[];
  readonly yourSeat: Seat | null;
  /** Solver moves still needed, counting down as the line is played. */
  readonly movesLeft: number;
  /** Cell of the most recent move, so the board can mark it. */
  readonly lastMoveCell: number | null;
  readonly winningLine: readonly number[] | null;
  /** The winning move in the position on screen, once the player asks to be told. */
  readonly shown: number | null;
  /** True while that answer is being worked out. */
  readonly asking: boolean;
  /** What the last wrong move cost, in solver moves. */
  readonly missed: number | null;
  /** Puzzles finished without a wrong move, and total attempted. */
  readonly solvedCount: number;
  readonly attempted: number;
  /** True while the run is holding a ready-made next puzzle. */
  readonly nextReady: boolean;
  readonly error: string | null;
}

interface RunState {
  readonly spec: GameSpec;
  readonly puzzle: Puzzle | null;
  readonly phase: PuzzlePhase;
  /** Every move from the empty board: the puzzle setup plus the line so far. */
  readonly moves: readonly number[];
  readonly game: State<Rules>;
  readonly movesLeft: number;
  readonly shown: number | null;
  /** A hint has been asked for and is being worked out. */
  readonly asking: boolean;
  readonly missed: number | null;
  readonly solvedCount: number;
  readonly attempted: number;
  /** Whether this puzzle has been failed, so a late solve is not counted clean. */
  readonly faulted: boolean;
  /** The puzzle hunted ahead of time, waiting for "next". */
  readonly queued: Puzzle | null;
  /** Rises whenever the position changes, to void in-flight worker replies. */
  readonly token: number;
  /** Seeds the hunt. Advanced per request so two hunts never collide. */
  readonly seed: number;
  readonly error: string | null;
}

type Action =
  | { readonly type: 'found'; readonly puzzle: Puzzle | null }
  | { readonly type: 'drop'; readonly param: number }
  | { readonly type: 'judged'; readonly verdict: PuzzleVerdict; readonly replyParam: number | null }
  | { readonly type: 'failed'; readonly message: string }
  | { readonly type: 'retry' }
  | { readonly type: 'show' }
  | { readonly type: 'shown'; readonly param: number | null }
  | { readonly type: 'next' };

/** Replay from the empty board. Returns the start position on any bad input. */
function replay(rules: Rules, firstSeat: number, params: readonly number[]): State<Rules> {
  let state = createInitial(rules, firstSeat as SeatOf<Rules>);
  for (const param of params) {
    if (state.status.kind !== 'in_play') break;
    const applied = applyMove(rules, state, state.toMove, { param });
    if (!applied.ok) break;
    state = applied.state;
  }
  return state;
}

function emptyGame(spec: GameSpec): State<Rules> {
  return createInitial(spec.rules, spec.rules.seats[0] as SeatOf<Rules>);
}

export function createRun(spec: GameSpec, seed = 1): RunState {
  return {
    spec,
    puzzle: null,
    phase: 'seeking',
    moves: [],
    game: emptyGame(spec),
    movesLeft: 0,
    shown: null,
    asking: false,
    missed: null,
    solvedCount: 0,
    attempted: 0,
    faulted: false,
    queued: null,
    token: 1,
    seed,
    error: null,
  };
}

/** Put a puzzle on the board, ready to be solved. */
function mount(state: RunState, puzzle: Puzzle): RunState {
  return {
    ...state,
    puzzle,
    phase: 'solving',
    moves: puzzle.setup,
    game: replay(state.spec.rules, puzzle.firstSeat, puzzle.setup),
    movesLeft: puzzle.winIn,
    shown: null,
    asking: false,
    missed: null,
    faulted: false,
    attempted: state.attempted + 1,
    token: state.token + 1,
    error: null,
  };
}

export function runReducer(state: RunState, action: Action): RunState {
  switch (action.type) {
    case 'found': {
      if (action.puzzle === null) {
        // Nothing provable turned up in the budget. Only fatal when there is
        // also nothing on the board: mid-run it just means no prefetch yet.
        return state.puzzle === null ? { ...state, phase: 'stuck' } : state;
      }
      // A puzzle arriving while one is being solved is the prefetch landing.
      if (state.puzzle !== null && state.phase !== 'seeking') {
        return { ...state, queued: action.puzzle };
      }
      return mount(state, action.puzzle);
    }

    case 'drop': {
      if (state.phase !== 'solving' || state.puzzle === null) return state;
      // The board is the authority on legality; a tap on a full column is not
      // an attempt and must not be judged as one.
      const legal = legalMoves(state.spec.rules, state.game).some(
        (move) => move.param === action.param,
      );
      if (!legal) return state;

      const applied = applyMove(state.spec.rules, state.game, state.game.toMove, {
        param: action.param,
      });
      if (!applied.ok) return state;

      return {
        ...state,
        phase: 'checking',
        moves: [...state.moves, action.param],
        game: applied.state,
        token: state.token + 1,
      };
    }

    case 'judged': {
      if (state.puzzle === null) return state;

      if (action.verdict.kind === 'solved') {
        return {
          ...state,
          phase: 'solved',
          movesLeft: 0,
          // Only a clean run counts: a puzzle solved after being told the
          // answer, or after a wrong turn, is a puzzle you were walked through.
          solvedCount: state.faulted ? state.solvedCount : state.solvedCount + 1,
        };
      }

      if (action.verdict.kind === 'onward') {
        // The defence answers in the same step as the verdict, so the board
        // never renders a position the player is not being asked about.
        const moves =
          action.replyParam === null ? state.moves : [...state.moves, action.replyParam];
        return {
          ...state,
          phase: 'solving',
          moves,
          game: replay(state.spec.rules, state.puzzle.firstSeat, moves),
          movesLeft: action.verdict.winIn,
          // The answer belonged to the previous position; keeping it would point
          // at the wrong column on the new one.
          shown: null,
          asking: false,
          token: state.token + 1,
        };
      }

      // Wrong, or an illegal move that slipped through. The position goes back
      // to what it was: the player is here to find the line, not to be punished
      // for one tap.
      return {
        ...state,
        phase: 'wrong',
        faulted: true,
        missed: action.verdict.kind === 'lost' ? action.verdict.wasWinIn : null,
        moves: state.moves.slice(0, -1),
        game: replay(state.spec.rules, state.puzzle.firstSeat, state.moves.slice(0, -1)),
        token: state.token + 1,
      };
    }

    case 'retry':
      if (state.phase !== 'wrong') return state;
      return { ...state, phase: 'solving', missed: null, token: state.token + 1 };

    /*
     * Asking to be told. The answer is fetched for the position on screen, not
     * read off the puzzle: `puzzle.key` is only the *first* move of the line,
     * so two moves in it points at a column that was right for a position
     * nobody is looking at any more.
     *
     * Marked as helped straight away rather than when the answer lands, so a
     * slow engine cannot be used to get a free look.
     */
    case 'show': {
      if (state.puzzle === null) return state;
      if (state.phase !== 'solving' && state.phase !== 'wrong') return state;
      return { ...state, phase: 'solving', asking: true, faulted: true, missed: null };
    }

    case 'shown':
      if (!state.asking) return state;
      return { ...state, asking: false, shown: action.param };

    case 'next': {
      const queued = state.queued;
      if (!queued) {
        // Nothing prefetched yet: go back to seeking rather than pretending.
        return { ...state, puzzle: null, phase: 'seeking', shown: null, missed: null };
      }
      return { ...mount(state, queued), queued: null };
    }

    case 'failed':
      return { ...state, phase: state.puzzle === null ? 'stuck' : 'wrong', error: action.message };

    default:
      return state;
  }
}

export interface UsePuzzlesOptions {
  readonly spec: GameSpec;
  readonly seed?: number;
  readonly minWin?: number;
  readonly maxWin?: number;
  /** Runs the engine. Defaults to a real `Worker`; tests pass their own. */
  readonly createWorker?: () => Worker;
  /** Shrinks the hunt budget, in positions. Tests use it to stay quick. */
  readonly maxPositions?: number;
  /** Shrinks the proof budget, in nodes. Tests use it to stay quick. */
  readonly maxNodes?: number;
  /**
   * An ISO date to play that day's puzzle instead of a fresh hunt.
   *
   * Set, the run is the daily: one position, the same one everybody else gets,
   * and no "next" - a daily you can reroll is just a puzzle list with a date on
   * it, and nothing to compare with anyone.
   */
  readonly date?: string;
}

export interface PuzzleControls {
  readonly run: PuzzleRun;
  readonly drop: (param: number) => void;
  readonly retry: () => void;
  readonly show: () => void;
  readonly next: () => void;
}

/** The cell a move landed in, for the board's "last move" marker. */
function lastCell(spec: GameSpec, moves: readonly number[]): number | null {
  if (moves.length === 0) return null;
  const rules = spec.rules;
  let state = createInitial(rules, rules.seats[0] as SeatOf<Rules>);
  let cell: number | null = null;
  for (const param of moves) {
    if (state.status.kind !== 'in_play') break;
    const applied = applyMove(rules, state, state.toMove, { param });
    if (!applied.ok) break;
    cell = applied.primary;
    state = applied.state;
  }
  return cell;
}

export function usePuzzles(options: UsePuzzlesOptions): PuzzleControls {
  const { spec } = options;
  const [state, dispatch] = useReducer(runReducer, undefined, () =>
    createRun(spec, options.seed ?? 1),
  );

  const workerRef = useRef<Worker | null>(null);
  const create = options.createWorker;

  useEffect(() => {
    const worker = create
      ? create()
      : new Worker(new URL('./puzzle.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;

    const onMessage = (event: MessageEvent<PuzzleResponse>) => {
      const message = event.data;
      if (message.kind === 'found') dispatch({ type: 'found', puzzle: message.puzzle });
      else if (message.kind === 'judged') {
        dispatch({ type: 'judged', verdict: message.verdict, replyParam: message.replyParam });
      } else if (message.kind === 'hinted') dispatch({ type: 'shown', param: message.param });
      else dispatch({ type: 'failed', message: message.message });
    };
    worker.addEventListener('message', onMessage);

    return () => {
      worker.removeEventListener('message', onMessage);
      worker.terminate();
      workerRef.current = null;
    };
  }, [create]);

  /** Seeds already sent, so a re-render cannot queue the same hunt twice. */
  const hunted = useRef(0);

  /*
   * Keep exactly one spare puzzle in hand.
   *
   * Runs both when there is nothing on the board and when there is nothing
   * queued behind it, which is what turns a multi-second hunt into something
   * the player never waits for.
   */
  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    if (state.phase === 'stuck') return;
    if (state.puzzle !== null && state.queued !== null) return;

    // One hunt in flight at a time: two would race for the same slot and the
    // loser's work would be thrown away.
    const wanted = state.attempted + (state.queued === null ? 1 : 0);
    if (hunted.current > wanted) return;
    hunted.current = wanted + 1;

    const request: FindMessage = {
      kind: 'find',
      rules: spec.rules,
      seed: state.seed + hunted.current * 7919,
      ...(options.date === undefined ? {} : { date: options.date }),
      ...(options.minWin === undefined ? {} : { minWin: options.minWin }),
      ...(options.maxWin === undefined ? {} : { maxWin: options.maxWin }),
      ...(options.maxPositions === undefined ? {} : { maxPositions: options.maxPositions }),
      token: state.token,
    };
    worker.postMessage(request);
  }, [
    spec,
    state.phase,
    state.puzzle,
    state.queued,
    state.attempted,
    state.seed,
    state.token,
    options.minWin,
    options.maxWin,
    options.maxPositions,
    options.date,
  ]);

  /*
   * Fetch the answer for the position on screen when one is asked for.
   *
   * Driven off `asking` rather than fired from the click, so a hint requested
   * and then invalidated by a move is simply never applied.
   */
  const asked = useRef(-1);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    if (!state.asking || state.puzzle === null) return;
    if (asked.current === state.token) return;
    asked.current = state.token;

    const request: HintMessage = {
      kind: 'hint',
      rules: spec.rules,
      theme: state.puzzle.theme,
      firstSeat: state.puzzle.firstSeat,
      params: state.moves,
      ...(options.maxNodes === undefined ? {} : { maxNodes: options.maxNodes }),
      token: state.token,
    };
    worker.postMessage(request);
  }, [spec, state.asking, state.puzzle, state.moves, state.token, options.maxNodes]);

  /** The position already sent for judging, so one move makes one request. */
  const judged = useRef(-1);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    if (state.phase !== 'checking' || state.puzzle === null) return;
    if (judged.current === state.token) return;
    judged.current = state.token;

    const request: MoveMessage = {
      kind: 'move',
      rules: spec.rules,
      theme: state.puzzle.theme,
      firstSeat: state.puzzle.firstSeat,
      params: state.moves,
      ...(options.maxNodes === undefined ? {} : { maxNodes: options.maxNodes }),
      token: state.token,
    };
    worker.postMessage(request);
  }, [spec, state.phase, state.puzzle, state.moves, state.token, options.maxNodes]);

  const run = useMemo<PuzzleRun>(() => {
    const status = state.game.status;
    const solving = state.phase === 'solving';
    return {
      spec: state.spec,
      puzzle: state.puzzle,
      phase: state.phase,
      game: state.game,
      board: state.game.board as readonly number[],
      // Nothing is playable unless it is your move in a live puzzle: a board
      // that accepted taps while the defence was thinking would queue moves
      // against a position nobody is looking at.
      legalColumns: solving
        ? legalMoves(state.spec.rules, state.game).map((move) => move.param)
        : [],
      yourSeat: (state.puzzle?.seat ?? null) as Seat | null,
      movesLeft: state.movesLeft,
      lastMoveCell: lastCell(state.spec, state.moves),
      winningLine: status.kind === 'win' ? status.line : null,
      shown: state.shown,
      asking: state.asking,
      missed: state.missed,
      solvedCount: state.solvedCount,
      attempted: state.attempted,
      nextReady: state.queued !== null,
      error: state.error,
    };
  }, [state]);

  return {
    run,
    drop: useCallback((param: number) => dispatch({ type: 'drop', param }), []),
    retry: useCallback(() => dispatch({ type: 'retry' }), []),
    show: useCallback(() => dispatch({ type: 'show' }), []),
    next: useCallback(() => dispatch({ type: 'next' }), []),
  };
}

type FindMessage = Extract<PuzzleRequest, { kind: 'find' }>;
type MoveMessage = Extract<PuzzleRequest, { kind: 'move' }>;
type HintMessage = Extract<PuzzleRequest, { kind: 'hint' }>;
